"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import {
    DEFAULT_FILES_QUERY,
    DEFAULT_FOLDERS_QUERY,
    FilesQuery,
    FoldersQuery,
    StorageFilterByType,
} from "../types";
import { View } from "../components/grids/types";
import {
    createViewingPreferencesStore,
    pickPersistableQuery,
    ROOT_FOLDER_KEY,
    ViewingScope,
} from "./viewingPreferences";

const DEFAULT_VIEW: View = "grid";

/** Owns the folders/files queries and grid/list views of the current folder.
 *  `loadFolder` swaps in the folder's own remembered choices. */
export function useViewingPreferences({
    persist,
    initialFilterBy,
}: {
    persist: boolean;
    initialFilterBy?: StorageFilterByType;
}) {
    const store = useMemo(
        () => createViewingPreferencesStore(persist),
        [persist]
    );
    const baseFilesQuery = useMemo<FilesQuery>(
        () => ({
            ...DEFAULT_FILES_QUERY,
            ...(initialFilterBy && { filterBy: initialFilterBy }),
        }),
        [initialFilterBy]
    );

    const folderKeyRef = useRef(ROOT_FOLDER_KEY);
    const [foldersQuery, setFoldersQuery] = useState<FoldersQuery>(
        DEFAULT_FOLDERS_QUERY
    );
    const [filesQuery, setFilesQuery] = useState<FilesQuery>(baseFilesQuery);
    const [views, setViews] = useState<Record<ViewingScope, View>>({
        folders: DEFAULT_VIEW,
        files: DEFAULT_VIEW,
    });

    const loadFolder = useCallback(
        (folderKey: string) => {
            folderKeyRef.current = folderKey;
            const stored = store.load(folderKey);
            setFoldersQuery({
                ...DEFAULT_FOLDERS_QUERY,
                ...stored.folders?.query,
            });
            setFilesQuery({ ...baseFilesQuery, ...stored.files?.query });
            setViews({
                folders: stored.folders?.view ?? DEFAULT_VIEW,
                files: stored.files?.view ?? DEFAULT_VIEW,
            });
        },
        [store, baseFilesQuery]
    );

    const updateFoldersQuery = useCallback(
        (changes: Partial<FoldersQuery>) => {
            setFoldersQuery((prev) => ({ ...prev, ...changes }));
            const query = pickPersistableQuery(changes);
            if (query) store.save(folderKeyRef.current, "folders", { query });
        },
        [store]
    );

    const updateFilesQuery = useCallback(
        (changes: Partial<FilesQuery>) => {
            setFilesQuery((prev) => ({ ...prev, ...changes }));
            const query = pickPersistableQuery(changes);
            if (query) store.save(folderKeyRef.current, "files", { query });
        },
        [store]
    );

    const updateView = useCallback(
        (scope: ViewingScope, view: View) => {
            setViews((prev) => ({ ...prev, [scope]: view }));
            store.save(folderKeyRef.current, scope, { view });
        },
        [store]
    );

    return {
        foldersQuery,
        filesQuery,
        views,
        loadFolder,
        updateFoldersQuery,
        updateFilesQuery,
        updateView,
    };
}
